import { FC, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import dayjs from 'dayjs'
import theme from 'theme'
import { AssetTrendPoint, TrendPeriod } from 'models'
import { useThemeColors } from 'hooks/useThemeColors'
import { ChartCardWrapper, ChartTooltipBox, StatSkeletonBar } from './statCard.styles'
import InlineSelect from './InlineSelect'
import { TREND_PERIOD_LABELS, TREND_PERIOD_OPTIONS } from '../assetInventory.constants'

const PERIOD_SPAN: Record<TrendPeriod, [number, dayjs.ManipulateType]> = {
  [TrendPeriod.OneWeek]: [7, 'day'],
  [TrendPeriod.OneMonth]: [1, 'month'],
  [TrendPeriod.ThreeMonths]: [3, 'month'],
  [TrendPeriod.SixMonths]: [6, 'month'],
  [TrendPeriod.PastYear]: [1, 'year']
}

const TICK_COUNT = 6

interface Props {
  title: string
  icon: JSX.Element
  unit: string
  points: AssetTrendPoint[]
  loading: boolean
  period: TrendPeriod
  onPeriodChange: (period: TrendPeriod) => void
}

const EntitiesOverTimeCard: FC<Props> = ({ title, icon, unit, points, loading, period, onPeriodChange }) => {
  const getColor = useThemeColors()
  const fontFamily = theme.typography.fontFamily.primary
  /* Figma axis labels: text-secondary, 12/500 with 0.5 tracking on x, 14/500 on y. */
  const axisFill = getColor('devices-secondary-text')
  const tick = { fill: axisFill, fontSize: 12, fontWeight: 500, letterSpacing: 0.5, fontFamily }
  const yTick = { fill: axisFill, fontSize: 14, fontWeight: 500, fontFamily }
  const lineColor = getColor('cta')

  /* The axis spans the selected period, not just the days that have data, so a tenant whose history
     starts mid-period still sees the window it asked for. */
  const { data, domain, ticks } = useMemo(() => {
    const series = points.map((point) => ({ ...point, ts: dayjs(point.date).startOf('day').valueOf() }))
    /* The data is as of yesterday, so ending the axis on today would draw a day nothing covers. */
    const end = series.length ? series[series.length - 1].ts : dayjs().subtract(1, 'day').startOf('day').valueOf()
    const [amount, unitOfTime] = PERIOD_SPAN[period] ?? PERIOD_SPAN[TrendPeriod.PastYear]
    const start = Math.min(dayjs(end).subtract(amount, unitOfTime).valueOf(), series.length ? series[0].ts : end)
    const step = (end - start) / (TICK_COUNT - 1)

    return {
      data: series,
      domain: [start, end] as [number, number],
      ticks: Array.from({ length: TICK_COUNT }, (_, i) => Math.round(start + step * i))
    }
  }, [points, period])
  const tickFormat = period === TrendPeriod.PastYear || period === TrendPeriod.SixMonths ? 'MMM YY' : 'MMM DD'

  return (
    <ChartCardWrapper>
      <Box className="stat-header">
        <Box className="stat-heading">
          <Box className="stat-icon">{icon}</Box>
          <Typography className="stat-title">{title}</Typography>
        </Box>
        <InlineSelect
          label="Time Period:"
          ariaLabel="Trend time period"
          value={period}
          options={TREND_PERIOD_OPTIONS}
          optionLabel={(option) => TREND_PERIOD_LABELS[option as TrendPeriod] ?? option}
          onChange={(value) => onPeriodChange(value as TrendPeriod)}
        />
      </Box>
      <Box className="chart-body">
        {loading ? (
          <StatSkeletonBar sx={{ width: '100%', height: '100%' }} />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={getColor('chart-grid')} strokeDasharray="4 4" vertical={false} />
              <XAxis
                dataKey="ts"
                type="number"
                scale="time"
                domain={domain}
                ticks={ticks}
                tick={tick}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value: number) => dayjs(value).format(tickFormat)}
                minTickGap={24}
              />
              <YAxis tick={yTick} axisLine={false} tickLine={false} width={48} allowDecimals={false} />
              <Tooltip
                cursor={{ stroke: getColor('chart-grid') }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null
                  const row = payload[0].payload as AssetTrendPoint

                  /* A night the pipeline missed is filled by carrying the last measured value
                     forward; the CPI charts' wording marks it so a flat run is not read as real. */
                  return (
                    <ChartTooltipBox>
                      <span className="tooltip-label">{dayjs(row.date).format('MMM DD, YYYY')}</span>
                      <span className="tooltip-value">
                        {row.value.toLocaleString()} {unit}
                      </span>
                      {row.estimated && <span className="tooltip-label">Estimated</span>}
                    </ChartTooltipBox>
                  )
                }}
              />
              <Area
                type="linear"
                dataKey="value"
                stroke={lineColor}
                strokeWidth={2}
                strokeLinecap="round"
                fill={getColor('perf-area-fill')}
                fillOpacity={1}
                activeDot={{ r: 4, fill: lineColor, stroke: 'none' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Box>
    </ChartCardWrapper>
  )
}

export default EntitiesOverTimeCard
